import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import validationSchema from './QuestionsValidation';
import Modal from '../../../CommonComponents/Modal';
import {
  getVerificationQuestions,
  resetQuestionsData,
  setSubscriberEnrolling,
  submitVerification,
} from '../../ExperianActions';
import {
  questionsLoadErrorsSelector,
  isFetchingQuestionsSelector,
  isSubmittingQuestionsSelector,
  questionsErrorsSelector,
  questionsSelector,
  subscriberEnrollingSelector,
} from '../../ExperianSelector';
import { Heading, ModalWrapper, SubHeading } from '../../ExperianStyles';
import QuestionsForm from './QuestionsForm';
import QuestionsLoading from './QuestionsLoading';
import WorkflowLanding from '../../components/WorkflowLanding/WorkflowLanding';
import Spinner from '../../../CommonComponents/Spinner';
import { Alert } from '../../../CommonComponents/Alert/Alert';
import ExperianContext from '../../ExperianContext';

const Questions = () => {
  const questions = useSelector(questionsSelector);
  const subscriberEnrolling = useSelector(subscriberEnrollingSelector);
  const isFetchingQuestions = useSelector(isFetchingQuestionsSelector);
  const isSubmittingQuestions = useSelector(isSubmittingQuestionsSelector);
  const errors = useSelector(questionsErrorsSelector);
  const questionsLoadErrors = useSelector(questionsLoadErrorsSelector);

  const { clientId, subscriberId } = useContext(ExperianContext);

  const dispatch = useDispatch();
  const resetSubscriberEnrolling = useCallback(() => dispatch(setSubscriberEnrolling('')), [
    dispatch,
  ]);

  const isOpen = subscriberEnrolling === subscriberId;

  useEffect(() => {
    if (isOpen) {
      dispatch(getVerificationQuestions(clientId, subscriberId));
    } else {
      dispatch(resetQuestionsData());
    }
  }, [clientId, subscriberId, isOpen, dispatch]);

  const initialValues = useMemo(
    () =>
      questions.reduce((acc, { QuestionNumber: questionNumber }) => {
        const key = `${questionNumber}`;
        acc[key] = '';
        return acc;
      }, {}),
    [questions],
  );

  const hasErrors = errors.length > 0 || questionsLoadErrors.length > 0;

  return (
    <>
      {isSubmittingQuestions && <Spinner />}
      <WorkflowLanding />
      <Modal isOpen={subscriberEnrolling === subscriberId}>
        <ModalWrapper>
          {errors.length > 0 && <Alert message={errors} />}
          {questionsLoadErrors.length > 0 && <Alert message={questionsLoadErrors} />}
          <Heading>Enroll in Online Identity and Financial Account Monitoring Services</Heading>
          {!hasErrors && <SubHeading>Verify Your Identity</SubHeading>}
          <Formik
            onSubmit={async (data, actions) => {
              await dispatch(submitVerification(clientId, subscriberId, data));
              actions.setSubmitting(false);
            }}
            initialValues={initialValues}
            validationSchema={() => validationSchema(initialValues)}
          >
            {({ values, setFieldValue, isValid, isSubmitting }) =>
              isFetchingQuestions ? (
                <QuestionsLoading />
              ) : (
                <QuestionsForm
                  isValid={isValid}
                  isSubmitting={isSubmitting}
                  questions={questions}
                  values={values}
                  setFieldValue={setFieldValue}
                  onCancel={resetSubscriberEnrolling}
                  hasErrors={hasErrors}
                />
              )
            }
          </Formik>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default Questions;
